/* CUSTOM CODE START: free-shipping-progress JS */
import { debounce, requestIdleCallback } from '@theme/utilities';

/**
 * Patch network APIs (fetch / XHR) once to notify callbacks on cart requests.
 * @param {any} g - global window object cast to any
 */
function patchNetworkAPIs(g) {
  if (g.__freeShippingProgressPatched) return;
  g.__freeShippingProgressPatched = true;

  /**
   * Notify registered callbacks when a cart-related URL is detected.
   * Avoid notifying for our own GET `/cart.js` fetch to prevent recursion.
   * @param {any} url
   * @param {string} method
   */
  const notifyIfCart = (url, method) => {
    try {
      if (typeof url !== 'string') return;
      // Ignore GET requests to /cart.js (internal fetch from update())
      if (method && method.toUpperCase() === 'GET' && /(^|\/)cart\.js(\?|$)/.test(url)) return;
      // Notify on cart change and add endpoints to react to cart modifications
      if (/\/cart\/(?:change|add|update)(?:[\/\.]|$)/.test(url)) {
        (g.__freeShippingProgressCallbacks || []).forEach(/** @param {() => void} cb */ function (cb) { try { cb(); } catch (e) {} });
      }
    } catch (e) {}
  };

  if (g.fetch) {
    /** @type {(...args: any[]) => Promise<any>} */
    const _origFetch = g.fetch.bind(g);
    /** @param {...any} args @returns {Promise<any>} */
    g.fetch = function (...args) {
      return _origFetch(...args).then((res) => {
        try {
          const candidate = args && args[0];
          const init = args && args[1];
          const url = typeof candidate === 'string' ? candidate : (candidate && candidate.url) || res.url || '';
          const method = (init && init.method) || (candidate && candidate.method) || 'GET';
          notifyIfCart(url, method);
        } catch (e) {}
        return res;
      });
    };
  }

  const _origXOpen = XMLHttpRequest.prototype.open;
  const _origXSend = XMLHttpRequest.prototype.send;
  /** @param {string} method @param {string} url */
  XMLHttpRequest.prototype.open = function (method, url) {
    try { /** @type {any} */ (this).__fsp_url = url; /** @type {any} */ (this).__fsp_method = method; } catch (e) {}
    return _origXOpen.apply(this, arguments);
  };
  /** @param {any} body */
  XMLHttpRequest.prototype.send = function (body) {
    this.addEventListener('load', function () {
      try {
        const url = /** @type {any} */ (this).__fsp_url || this.responseURL || '';
        const method = /** @type {any} */ (this).__fsp_method || 'GET';
        notifyIfCart(url, method);
      } catch (e) {}
    });
    return _origXSend.apply(this, arguments);
  };
}

  /**
   * FreeShippingProgress instance manager
   * Attaches behavior to any element with the `data-free-shipping` attribute.
   */
  class FreeShippingProgress {
    /** @param {HTMLElement} root */
    constructor(root) {
      this.root = root;
      this.threshold = Number(root.dataset.thresholdCents || 0);
      this.templateBelow = root.dataset.messageBelow || 'Mancano {{amount}} per la spedizione gratuita';
      this.templateMet = root.dataset.messageMet || 'Spedizione gratuita raggiunta';
      this.currency = root.dataset.currency || 'EUR';
      this.output = /** @type {HTMLElement | null} */ (root.querySelector('.free-shipping-progress__text'));

      this.update = debounce(this.update.bind(this), 800);
      requestIdleCallback(() => this.update());

      // rate-limiting / caching to avoid frequent /cart.js fetches
      /** @type {number} */ this._lastFetchAt = 0;
      /** @type {number|null} */ this._lastTotal = null;
      /** Whether we've successfully shown the shipping text at least once. */
      this._hasSucceeded = false;

      // wrapper to handle cart change notifications: reset success flag and retries, then update
      this._onCartChange = () => {
        try {
          this._hasSucceeded = false;
        } catch (e) {}
        this.update();
      };

      // Use an `any` alias for `window` to avoid TS errors in editor checks
      const g = /** @type {any} */ (window);

      // Global callback registry so patched network APIs can notify instances.
      // We rely on network intercepts (fetch/XHR -> /cart/change) to notify only
      // on real cart changes, avoiding noisy document/window events.
      g.__freeShippingProgressCallbacks = g.__freeShippingProgressCallbacks || /** @type {Array<() => void>} */ ([]);
      try { g.__freeShippingProgressCallbacks.push(this._onCartChange); } catch (e) {}

      // Patch network APIs once per page.
      try { patchNetworkAPIs(g); } catch (e) {}

      // keep only native Shopify cart events; avoid broad DOM observation for performance
      // (we already listen to `cart:updated` and `cart:refresh` above)
    }

    destroy() {
      const g = /** @type {any} */ (window);
      // Remove this instance from global callbacks (if present)
      try {
        if (g.__freeShippingProgressCallbacks) {
          const i = g.__freeShippingProgressCallbacks.indexOf(this._onCartChange);
          if (i > -1) g.__freeShippingProgressCallbacks.splice(i, 1);
        }
      } catch (e) {}
      // nothing else to clean up since we rely on native Shopify events only
    }

    /** @returns {Promise<any|null>} */
    async #fetchCart() {
      try {
        const res = await fetch('/cart.js', { credentials: 'same-origin' });
        if (!res.ok) return null;
        return await res.json();
      } catch (e) {
        return null;
      }
    }

    /* CUSTOM CODE: shared cart fetch to avoid duplicate /cart.js calls across instances */
    /** @returns {Promise<any|null>} */
    async #fetchCartShared() {
      const g = /** @type {any} */ (window);
      const now = Date.now();

      if (g.__fspCartCache && now - g.__fspCartCache.at < 1000) {
        return g.__fspCartCache.data;
      }

      if (g.__fspCartFetchPromise) return g.__fspCartFetchPromise;

      g.__fspCartFetchPromise = this.#fetchCart()
        .then((data) => {
          g.__fspCartCache = { at: Date.now(), data };
          g.__fspCartFetchPromise = null;
          return data;
        })
        .catch(() => {
          g.__fspCartFetchPromise = null;
          return null;
        });

      return g.__fspCartFetchPromise;
    }
    /* END CUSTOM CODE */

    /** @param {number} cents */
    #formatMoney(cents) {
      try {
        const locale = document.documentElement.lang || 'en-US';
        const value = cents / 100;
        return new Intl.NumberFormat(locale, { style: 'currency', currency: this.currency }).format(value);
      } catch (e) {
        return (cents / 100).toFixed(2) + ' ' + this.currency;
      }
    }

    async   update() {
      const now = Date.now();
      // skip if we fetched recently to avoid storms (1s)
      if (this._lastFetchAt && now - this._lastFetchAt < 1000) return;
      this._lastFetchAt = now;

      /* CUSTOM CODE: use shared cart fetch to reduce duplicate requests */
      const cart = await this.#fetchCartShared();
      /* END CUSTOM CODE */
      const total = cart && typeof cart.total_price !== 'undefined' ? Number(cart.total_price) : 0;

      if (!this.output) return;

      // robustly compute missing amount (both values in cents)
      const missing = Math.max(this.threshold - total, 0);

      // If no threshold is configured (<= 0) or the threshold is reached, show met message
      if (this.threshold <= 0 || missing === 0) {
        this.output.textContent = this.templateMet;
        this.root.classList.remove('free-shipping-progress--loading');
        const inner = this.root.querySelector('.free-shipping-progress__inner');
        if (inner instanceof HTMLElement) inner.style.visibility = '';
        void this.root.offsetHeight;
        this._lastTotal = total;
        this._hasSucceeded = true;
        return;
      }

      const formatted = this.#formatMoney(missing);
      this.output.textContent = this.templateBelow.replace(/{{\s*amount\s*}}|%\{amount\}|\[\[amount\]\]/g, formatted);
      this.root.classList.remove('free-shipping-progress--loading');
      const inner = this.root.querySelector('.free-shipping-progress__inner');
      if (inner instanceof HTMLElement) inner.style.visibility = '';
      void this.root.offsetHeight;
      this._lastTotal = total;
      this._hasSucceeded = true;
    }
  }

  // Initialize instances for existing and future markup
  const _instances = [];
  /**
   * Initialize any uninitialized `[data-free-shipping]` elements under `root`.
   * Marks elements with `data-fsp-initialized` to avoid double-init.
   * @param {ParentNode} [root=document]
   */
  function initFreeShippingProgress(root = document) {
    root.querySelectorAll('[data-free-shipping]').forEach((el) => {
      if (!(el instanceof HTMLElement)) return;
      if (el.dataset.fspInitialized) return;
      try {
        _instances.push(new FreeShippingProgress(el));
        el.dataset.fspInitialized = '1';
      } catch (e) {
        // ignore per-instance init errors
      }
    });
  }

  // init existing
  initFreeShippingProgress();

  // observe DOM for dynamically injected drawers/partials
  try {
    const mo = new MutationObserver((mutations) => {
      for (const m of mutations) {
        for (const node of m.addedNodes) {
          if (!(node instanceof HTMLElement)) continue;
          // if the added node itself is a free-shipping element, init it; else init any children
          if (node.matches && node.matches('[data-free-shipping]')) initFreeShippingProgress(node);
          else initFreeShippingProgress(node);
        }
      }
    });
    mo.observe(document.body, { childList: true, subtree: true });
  } catch (e) {
    // MutationObserver may not be available in some environments; ignore
  }

/* CUSTOM CODE END: free-shipping-progress JS */